/**
 * Lobby scene — three-column layout matching the WhatIs polish bar:
 *
 *   left  | center            | right
 *   --------------------------------------
 *   TOP10 | "SURVEY SAYS"     | STANDINGS
 *   HOWTO | tagline           | (ping-pong scroll)
 *
 * Top-10 + how-to-play come from the shared lobby-leaderboard / lobby-howto
 * helpers. The center column shows the SURVEY SAYS title with a soft pulse
 * + a CTA. The right column displays ranks 11+ with the same ping-pong
 * scroll the WhatIs lobby uses.
 */

import type { LbRow } from '@sg/shared';
import { createLobbyLeaderboardTop, createLobbyLeaderboardTail } from '../ui/lobby-leaderboard.js';
import { createLobbyHowTo } from '../ui/lobby-howto.js';

export interface LobbyHandle {
  el: HTMLDivElement;
  setLeaderboard(rows: LbRow[], allTime: LbRow[]): void;
  setVisible(v: boolean): void;
}

const LOBBY_STYLES = `
  @keyframes ss-title-pulse {
    0%, 100% { transform: scale(1); text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 30px rgba(255,196,61,0.4); }
    50%      { transform: scale(1.02); text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 60px rgba(255,196,61,0.7); }
  }
  .ss-lobby {
    position: absolute; inset: 0;
    display: grid;
    grid-template-columns: 380px 1fr 380px;
    gap: 32px;
    padding: 60px 60px;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
  }
  .ss-lobby-left {
    display: flex; flex-direction: column; gap: 20px;
    min-height: 0;
  }
  .ss-lobby-center {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center;
    gap: 24px;
  }
  .ss-lobby-right {
    display: flex; flex-direction: column;
    min-height: 0;
  }
  .ss-title-survey {
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 200px; line-height: 0.9;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 8px;
    text-shadow: 0 4px 0 var(--sg-secondary, #060CE9), 0 0 20px rgba(255,196,61,0.4);
  }
  .ss-title-says {
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 320px; line-height: 0.9;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 14px;
    text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 30px rgba(255,196,61,0.4);
    animation: ss-title-pulse 2.6s ease-in-out infinite;
  }
  .ss-tagline {
    margin-top: 4px;
    font-size: 28px;
    color: var(--sg-text-on-secondary, #FFC43D);
    opacity: 0.9;
    max-width: 880px;
    line-height: 1.3;
  }
  .ss-cta {
    margin-top: 4px;
    font-size: 22px;
    letter-spacing: 0.2em;
    color: var(--sg-accent, #E91E63);
    background: rgba(0,0,0,0.35);
    border: 2px solid var(--sg-accent, #E91E63);
    border-radius: 999px;
    padding: 10px 26px;
    text-transform: uppercase;
    box-shadow: 0 0 20px rgba(233,30,99,0.25);
  }
`;

function ensureLobbyStyles(): void {
  if (document.getElementById('ss-lobby-style')) return;
  const s = document.createElement('style');
  s.id = 'ss-lobby-style';
  s.textContent = LOBBY_STYLES;
  document.head.appendChild(s);
}

export function createLobby(stage: HTMLElement): LobbyHandle {
  ensureLobbyStyles();
  const el = document.createElement('div');
  el.className = 'ss-lobby';
  el.setAttribute('data-scene', 'lobby');
  el.setAttribute('data-testid', 'ss-lobby');

  // Left column: top-10 leaderboard + how-to-play
  const left = document.createElement('div');
  left.className = 'ss-lobby-left';
  const lbTop = createLobbyLeaderboardTop(left);
  const howTo = createLobbyHowTo(left);
  void howTo;

  // Center column: title + tagline
  const center = document.createElement('div');
  center.className = 'ss-lobby-center';
  const titleSmall = document.createElement('div');
  titleSmall.className = 'ss-title-survey';
  titleSmall.textContent = 'SURVEY';
  const titleBig = document.createElement('div');
  titleBig.className = 'ss-title-says';
  titleBig.textContent = 'SAYS';
  const tagline = document.createElement('div');
  tagline.className = 'ss-tagline';
  tagline.textContent = 'We surveyed 100 people — match the top answers and rack up the points!';
  const cta = document.createElement('div');
  cta.className = 'ss-cta';
  cta.textContent = 'Streamer types !start to begin';
  center.appendChild(titleSmall);
  center.appendChild(titleBig);
  center.appendChild(tagline);
  center.appendChild(cta);

  // Right column: ranks 11+ ping-pong
  const right = document.createElement('div');
  right.className = 'ss-lobby-right';
  const lbTail = createLobbyLeaderboardTail(right);

  el.appendChild(left);
  el.appendChild(center);
  el.appendChild(right);
  stage.appendChild(el);

  return {
    el,
    setLeaderboard(rows, allTime) {
      // Top-10 panel always shows the all-time top (or whatever the first arg
      // is when the snapshot doesn't carry an explicit all-time list).
      const useRows = allTime.length > 0 ? allTime : rows;
      lbTop.setRows(useRows);
      lbTail.setRows(useRows);
    },
    setVisible(v) {
      el.style.display = v ? 'grid' : 'none';
    },
  };
}
